/**
 * What the month-run worker is doing, for any screen that wants to say so.
 *
 * Its own file since fastphone-4: the header's pill reads this on every page,
 * and while it lived in useMonthRunDriver.ts the pill pulled the whole worker
 * (the queue, the month-run builder) into the code the first screen waits for.
 * The worker writes it through `setDriverStatus`, `noteWorking` and `noteRested`.
 */
import { useSyncExternalStore } from 'react';

export type DriverMode =
    /** No office, or the worker is not mounted. */
    | 'off'
    /** Another tab in this browser holds the office's worker. */
    | 'standby'
    /** Asking, or asleep until the next return comes due. */
    | 'idle'
    /** Working a return this minute. */
    | 'working'
    /** The queue could not be asked (not applied yet, refused, offline). */
    | 'unavailable'
    /** gm-9 E6: the office's server engine is carrying this run; this tab stands by. */
    | 'engine';

export interface DriverWork { orgName: string; form: string; periodKey: string | null; periodLabel: string | null; text: string }

export interface DriverStatus {
    mode: DriverMode;
    /** The return being worked, while `mode` is `working`. */
    working: DriverWork | null;
    /** When the next waiting return comes due, while idle. */
    nextWakeAt: string | null;
    /** How many returns this tab has carried since it opened. */
    carried: number;
    /**
     * atrest step 7: the return worked last, and the office it belongs to, kept
     * for `RECENT_LINGER_MS` after the work stops. The app's pill reads this, so
     * it stays up across the moment between two returns instead of blinking.
     * `active` is false once nothing is being worked.
     */
    recent: { officeOrgId: string; work: DriverWork; active: boolean } | null;
}

export const RECENT_LINGER_MS = 20_000;

const OFF: DriverStatus = { mode: 'off', working: null, nextWakeAt: null, carried: 0, recent: null };
let status: DriverStatus = OFF;

/** The status now, outside React. */
export function driverStatus(): DriverStatus { return status; }
const listeners = new Set<() => void>();
let lingering: ReturnType<typeof setTimeout> | null = null;

export function setDriverStatus(next: Partial<DriverStatus>): void {
    status = { ...status, ...next };
    for (const l of listeners) l();
}

/** A return is in hand. */
export function noteWorking(officeOrgId: string, work: DriverWork): void {
    if (lingering) { clearTimeout(lingering); lingering = null; }
    setDriverStatus({ mode: 'working', working: work, recent: { officeOrgId, work, active: true } });
}

/** Nothing is in hand any more: say so now, and forget the last return after the linger. */
export function noteRested(next: Partial<DriverStatus>): void {
    const recent = status.recent;
    setDriverStatus({ ...next, working: null, recent: recent ? { ...recent, active: false } : null });
    if (recent && !lingering) {
        lingering = setTimeout(() => { lingering = null; setDriverStatus({ recent: null }); }, RECENT_LINGER_MS);
    }
}

/** Test seam: back to a worker that has never started. */
export function resetDriverStatus(): void {
    if (lingering) { clearTimeout(lingering); lingering = null; }
    status = OFF;
    for (const l of listeners) l();
}

export function useMonthRunDriverStatus(): DriverStatus {
    return useSyncExternalStore(
        (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
        () => status,
        () => OFF,
    );
}
